import { Component, OnInit } from '@angular/core';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { LeagueWeek } from '../../models/LeagueWeek';
import { GolfEventService } from '../../services/golf-event.service';
import { GolfEvent } from '../../models/GolfEvent';
import { WeeklyRoundListComponent } from './weekly-round-list/weekly-round-list.component';
import { PERMISSIONS, EVENT_FORMAT } from '../../models/constants';
import { AuthenticationService } from '../../services/authentication.service';
import { CreateWeekComponent } from './create-week/create-week.component';
import { FieldComponent } from './field/field.component';
import { ApplicationService } from '../../services/application.service';
import { GolfEventField } from '../../models/golfeventfield';
import { CreateTeamComponent } from './create-team/create-team.component';
import { LeagueLeaderboardComponent } from './league-leaderboard/league-leaderboard.component';
import { WeekDetailsComponent } from './week-details/week-details.component';
import { isEventOver, toCalendarControlFriendlyDate } from '../../util/dateutil';

@Component({
  selector: 'app-league',
  standalone: true,
  imports: [NgFor, NgIf, NgClass, WeeklyRoundListComponent, CreateWeekComponent, FieldComponent, CreateTeamComponent, LeagueLeaderboardComponent, WeekDetailsComponent],
  templateUrl: './league.component.html',
  styleUrl: './league.component.css'
})
export class LeagueComponent implements OnInit {
  constructor(private route: ActivatedRoute, private golfEventService:GolfEventService, private authenticationService:AuthenticationService, private applicationService:ApplicationService){}

  loggedInPlayerId:number|null = null;
  league:GolfEvent|null = null;
  eventId:number|null = null;
  leagueWeeks:Array<LeagueWeek> = [];
  selectedWeek:LeagueWeek|null = null;
  weekBeingEdited:LeagueWeek|null = null;
  containerDisplay="LEADERBOARD";
  canAddWeek:boolean = false;
  paymentLink:string = '';
  hasPaidEntryFee:boolean = false;
  paidPlayers:Array<GolfEventField> = [];
  isTeamEvent:boolean = false;
  formatType:string = EVENT_FORMAT.MULTI_WEEK_LEAGUE;
  openPostTrigger:number = 0;
  isPostingScore:boolean = false;
  showCoursePicker:boolean = false;

  ngOnInit(): void {
    this.eventId = Number(this.route.snapshot.paramMap.get('id'));
    if(this.authenticationService.loginResult && this.authenticationService.loginResult.player)
      this.loggedInPlayerId = this.authenticationService.loginResult.player.playerId;

    this.setupPermissions();
    this.getLeague(this.eventId);
    this.getLeagueWeeks(this.eventId);
  }

  async setupPermissions():Promise<void> {
      this.canAddWeek = await this.authenticationService.hasPermission(PERMISSIONS.EVENTADMIN);
      await this.getFieldThatHasPaid();
      this.hasPaidEntryFee = this.paidPlayers.filter(p => p.playerId === this.loggedInPlayerId).length > 0;
  }

  async getLeague(eventId:number) {
    this.league = await this.golfEventService.getGolfEventById(eventId);
    this.formatType = this.league.type === 'LEAGUE' ? EVENT_FORMAT.MULTI_WEEK_LEAGUE : this.league.type;
    // Hybrid scramble pairs up per round, so there are no fixed teams to create or join.
    this.isTeamEvent = this.league.isTeamEvent && !this.isHybridScramble;
    this.paymentLink = this.golfEventService.generatePaymentLink(this.league);
  }

  async getLeagueWeeks(eventId:number) {
    this.applicationService.showGlobalSpinner();
    this.leagueWeeks = await this.golfEventService.getLeagueWeeks(eventId);
    this.applicationService.hideGlobalSpinner();
    this.syncSingleCourseView();
  }

  async getFieldThatHasPaid() {
    if(!this.eventId) return;
    this.paidPlayers = await this.golfEventService.searchField(this.eventId, true);
  }

  showPlaySection():void {
    this.isPostingScore = false;
    if (this.isSingleCourse && this.leagueWeeks.length === 1) {
      this.selectWeek(this.leagueWeeks[0]);
      return;
    }
    this.containerDisplay = 'WEEKLIST';
  }

  showLeaderboard():void {
    this.isPostingScore = false;
    this.containerDisplay = 'LEADERBOARD';
  }

  showField():void {
    this.isPostingScore = false;
    this.containerDisplay = 'FIELD';
  }

  showInfo():void {
    this.isPostingScore = false;
    this.containerDisplay = 'INFO';
  }

  syncSingleCourseView():void {
    if (this.isSingleCourse && this.leagueWeeks.length === 1 && this.containerDisplay === 'WEEKLIST') {
      this.selectWeek(this.leagueWeeks[0]);
    }
  }

  get isSingleCourse():boolean {
    return this.formatType === EVENT_FORMAT.SINGLE_COURSE;
  }

  get isMultiCourse():boolean {
    return this.formatType === EVENT_FORMAT.MULTI_COURSE || this.isHybridScramble;
  }

  get isHybridScramble():boolean {
    return this.formatType === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  get isMultiWeekLeague():boolean {
    return this.formatType === EVENT_FORMAT.MULTI_WEEK_LEAGUE || this.formatType === 'LEAGUE' || !this.formatType;
  }

  get canManageSlots():boolean {
    return this.canAddWeek && !this.isSingleCourse && !this.isEventOver;
  }

  get isEventOver():boolean {
    return isEventOver(this.league?.endDate);
  }

  get playTabLabel():string {
    if (this.isSingleCourse) return 'Rounds';
    if (this.isMultiCourse) return 'Courses';
    return 'Weeks';
  }

  get formatLabel():string {
    if (this.isHybridScramble) return 'Hybrid Scramble';
    if (this.isSingleCourse) return 'Single Course';
    if (this.isMultiCourse) return 'Multi-Course';
    return 'Multi-Week League';
  }

  get scheduleColumnLabel():string {
    return this.isMultiWeekLeague ? 'Week' : 'Course';
  }

  get addSlotLabel():string {
    return this.isMultiWeekLeague ? 'Add Week' : 'Add Course';
  }

  get coursePickerTitle():string {
    return this.isMultiWeekLeague ? 'Which Week?' : 'Which Course?';
  }

  get isPlayTabActive():boolean {
    return this.containerDisplay === 'WEEKLIST'
      || this.containerDisplay === 'WEEKINFO'
      || this.containerDisplay === 'CREATE'
      || this.containerDisplay === 'EDITWEEK';
  }

  get showStickyActions():boolean {
    return !this.isEventOver
      && this.containerDisplay !== 'CREATETEAM'
      && this.containerDisplay !== 'CREATE'
      && this.containerDisplay !== 'EDITWEEK'
      && !this.isPostingScore;
  }

  get eventStartDateInput():string {
    return this.league?.startDate ? toCalendarControlFriendlyDate(new Date(this.league.startDate)) : '';
  }

  get eventEndDateInput():string {
    return this.league?.endDate ? toCalendarControlFriendlyDate(new Date(this.league.endDate)) : '';
  }

  selectWeek(week:LeagueWeek) {
    this.selectedWeek = week;
    this.weekBeingEdited = null;
    this.containerDisplay = 'WEEKINFO';
  }

  editWeek(week:LeagueWeek):void {
    this.weekBeingEdited = week;
    this.containerDisplay = 'EDITWEEK';
  }

  requestPostScore():void {
    if (!this.hasPaidEntryFee || this.isEventOver) return;

    if (this.isSingleCourse && this.leagueWeeks.length === 1) {
      this.openPostForWeek(this.leagueWeeks[0]);
      return;
    }

    if (this.containerDisplay === 'WEEKINFO' && this.selectedWeek) {
      this.openPostTrigger++;
      return;
    }

    this.showCoursePicker = true;
  }

  openPostForWeek(week:LeagueWeek):void {
    this.showCoursePicker = false;
    this.selectedWeek = week;
    this.weekBeingEdited = null;
    this.containerDisplay = 'WEEKINFO';
    // Defer so the round list mounts first; otherwise the trigger looks like a stale firstChange.
    setTimeout(() => this.openPostTrigger++, 0);
  }

  closeCoursePicker():void {
    this.showCoursePicker = false;
  }

  onPostingChange(isPosting:boolean):void {
    this.isPostingScore = isPosting;
  }

  formatDate(date: string | Date | undefined): string {
    return this.applicationService.formatUTCDate(date);
  }

  formatHolesToPlay(holesToPlay:string|undefined):string {
    if (!holesToPlay) return '';
    return this.applicationService.formatHolesToPlay(holesToPlay);
  }

  formatYesNo(value:boolean|undefined):string {
    return value ? 'Yes' : 'No';
  }

  formatAggregation(value:string|undefined):string {
    if (value === 'AVG') return 'Average of best rounds';
    return 'Sum of best rounds';
  }

  get scoringLabel():string {
    if (this.isHybridScramble) return 'Best net rounds averaged per course, then course scores added up';
    return this.formatAggregation(this.league?.scoreAggregation);
  }

  async refreshWeeks():Promise<void> {
    if(!this.eventId) return;
    const selectedWeekId = this.selectedWeek?.id ?? this.weekBeingEdited?.id;
    await this.getLeagueWeeks(this.eventId);
    if (selectedWeekId) {
      const refreshed = this.leagueWeeks.find(w => w.id === selectedWeekId) || null;
      this.selectedWeek = refreshed;
      this.weekBeingEdited = null;
      if (refreshed) {
        this.containerDisplay = 'WEEKINFO';
        return;
      }
    }
    this.weekBeingEdited = null;
    this.containerDisplay = this.isSingleCourse ? 'WEEKINFO' : 'WEEKLIST';
  }
}
